// Per-deal password for a shared IC Memo link (DM, 2026-11-02).
//
// Someone outside Proxy who has a memo's link and its password can read the
// memo and comment; signed-in Proxy users never see the prompt. A deal with
// no password is not shared: the link asks outsiders to sign in.
//
// WHERE IT LIVES. One app_settings row per deal, key ic_memo_password:<deal id>,
// value = JSON { hash, salt, iter, set_by, set_at }. Never the password itself:
// a PBKDF2-SHA-256 hash with a random salt, slow enough (200k rounds) that the
// stored value is not a shortcut to the password.
//
// WHAT IT IS AND IS NOT. A front-door lock, the same strength as the Microsoft
// sign-in in front of the rest of Proxy: both are checks in the page, and the
// data behind them is read with the app's public key. It keeps a forwarded link
// away from people who were not given the password; it is not a vault.

import { supabase } from './supabase'

const KEY = (dealId) => `ic_memo_password:${dealId}`
const ITER = 200000
const SESSION = (dealId) => `icmemo-unlocked:${dealId}`

const b64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)))
const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0))

async function derive(password, saltB64, iter) {
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveBits'])
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt: unb64(saltB64), iterations: iter }, key, 256)
  return b64(bits)
}

export async function loadShare(dealId) {
  if (!dealId) return null
  const { data, error } = await supabase.from('app_settings').select('value').eq('key', KEY(dealId)).maybeSingle()
  if (error || !data?.value) return null
  try { return JSON.parse(data.value) } catch { return null }
}

export async function setSharePassword(dealId, password, setBy) {
  const salt = b64(crypto.getRandomValues(new Uint8Array(16)))
  const hash = await derive(password, salt, ITER)
  const value = JSON.stringify({ hash, salt, iter: ITER, set_by: setBy || null, set_at: new Date().toISOString() })
  const { error } = await supabase.from('app_settings').upsert({ key: KEY(dealId), value }, { onConflict: 'key' })
  return error ? error.message : null
}

export async function clearShare(dealId) {
  const { error } = await supabase.from('app_settings').delete().eq('key', KEY(dealId))
  return error ? error.message : null
}

export async function checkPassword(share, password) {
  if (!share?.hash || !password) return false
  return (await derive(password, share.salt, share.iter || ITER)) === share.hash
}

// An unlock lasts for the browser tab's session, and only for the password it
// was made with: change the password and every open unlock lapses.
export function isUnlocked(dealId, share) {
  try { return !!share?.hash && sessionStorage.getItem(SESSION(dealId)) === share.hash } catch { return false }
}
export function rememberUnlock(dealId, share) {
  try { sessionStorage.setItem(SESSION(dealId), share.hash) } catch { /* private mode: asks again on reload */ }
}
